import React, { useState } from 'react';

export default function PatientHistorySection({ history = [], patientID, patientName, currentOrganism, data }) {
  const [activeView, setActiveView] = useState('timeline'); // 'timeline' | 'longitudinal'
  const [searchTerm, setSearchTerm] = useState('');
  const [specimenFilter, setSpecimenFilter] = useState('ALL');

  const specimens = ['ALL', ...new Set(history.map((h) => h.specimenName).filter(Boolean))];

  // Recurring organism detection
  const recurringPastEpisodes = history.filter((h) => {
    if (!currentOrganism) return false;
    return (
      h.organismSummary &&
      h.organismSummary.toLowerCase().includes(currentOrganism.toLowerCase())
    );
  });

  const filteredHistory = history.filter((h) => {
    const matchesSpecimen = specimenFilter === 'ALL' || h.specimenName === specimenFilter;
    const matchesSearch =
      !searchTerm ||
      (h.organismSummary && h.organismSummary.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (h.cultureStatus && h.cultureStatus.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (h.specimenName && h.specimenName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (h.resistantDrugsSummary && h.resistantDrugsSummary.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSpecimen && matchesSearch;
  });

  // Current case chronological audit events
  const auditEvents = [
    {
      time: data?.receivedDate ? new Date(data.receivedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '09:00 AM',
      date: data?.receivedDate ? new Date(data.receivedDate).toLocaleDateString() : 'Today',
      action: 'Sample Received & Inoculated',
      user: data?.technicianName || 'Lab Technician',
      status: 'Received',
      detail: `Specimen ${data?.specimenName || 'Standard'} received at laboratory accession desk and plated.`
    },
    {
      time: data?.inoculationDate ? new Date(data.inoculationDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '09:15 AM',
      date: data?.receivedDate ? new Date(data.receivedDate).toLocaleDateString() : 'Today',
      action: 'Direct Microscopy Logged',
      user: data?.technicianName || 'Microbiology Staff',
      status: data?.gramStain ? 'Completed' : 'Pending',
      detail: data?.gramStain || 'Direct smear examined and recorded.'
    },
    {
      time: '11:30 AM',
      date: 'Today',
      action: 'Culture & Incubation Updated',
      user: 'Microbiology Bench Tech',
      status: data?.cultureStatus || 'Pending',
      detail: `Status set to "${data?.cultureStatus || 'Pending'}". Colony count: ${data?.colonyCount || 'Pending'}.`
    },
    ...(data?.isolates || []).map((iso, i) => ({
      time: '02:15 PM',
      date: 'Today',
      action: `Isolate #${i + 1} Identified & AST Evaluated`,
      user: data?.doctorName || 'Senior Microbiologist',
      status: 'ID & AST Saved',
      detail: `${iso.organismName} (${iso.gramType || 'Bacteria'}) — ${iso.astResults?.length || 0} antimicrobials tested.`
    })),
    ...(data?.approved ? [{
      time: data.approvedAt ? new Date(data.approvedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '04:00 PM',
      date: data.approvedAt ? new Date(data.approvedAt).toLocaleDateString() : 'Today',
      action: 'Final Report Authorized & Released',
      user: data.approvedBy || 'Authorizing Pathologist',
      status: 'Released',
      detail: 'Final diagnostic report approved with electronic signature stamp and SHA-256 seal.'
    }] : [])
  ];

  return (
    <div className="wb-stage-view">
      {/* Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">Case Audit Trail & Longitudinal History</h3>
          <span className="wb-stage-subtitle">
            UHID: <strong>{patientID}</strong> &bull; Patient: <strong>{patientName}</strong>
          </span>
        </div>

        {/* View Switcher */}
        <div className="wb-segmented-control" style={{ width: '320px' }}>
          <button
            type="button"
            className={`wb-seg-btn ${activeView === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveView('timeline')}
          >
            📋 Case Audit Timeline
          </button>
          <button
            type="button"
            className={`wb-seg-btn ${activeView === 'longitudinal' ? 'active' : ''}`}
            onClick={() => setActiveView('longitudinal')}
          >
            📊 Prior History ({history.length})
          </button>
        </div>
      </div>

      {activeView === 'timeline' ? (
        /* Timeline / Audit Table Hybrid */
        <div className="wb-card-clean">
          <div className="wb-table-wrapper">
            <table className="wb-clinical-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Time & Date</th>
                  <th style={{ width: '220px' }}>Workflow Action</th>
                  <th style={{ width: '160px' }}>User / Role</th>
                  <th style={{ width: '110px' }}>Status</th>
                  <th>Clinical Observation / Change Details</th>
                </tr>
              </thead>
              <tbody>
                {auditEvents.map((evt, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{evt.time}</strong>
                      <div className="wb-cell-subtext">{evt.date}</div>
                    </td>
                    <td>
                      <strong style={{ color: '#0f172a' }}>{evt.action}</strong>
                    </td>
                    <td>
                      <span className="wb-tag-neutral">{evt.user}</span>
                    </td>
                    <td>
                      <span className="wb-status-pill wb-status-received">{evt.status}</span>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#334155' }}>
                      {evt.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Longitudinal Past Cultures Table */
        <div>
          {/* Filter Bar for Past History */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '10px' }}>
            <div className="wb-ast-search-box" style={{ flex: 1, maxWidth: '300px' }}>
              <span style={{ color: '#94a3b8' }}>🔍</span>
              <input
                type="text"
                placeholder="Search past organisms, drugs..."
                className="wb-ast-search-input"
                style={{ width: '100%' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="wb-clear-btn"
                  onClick={() => setSearchTerm('')}
                >
                  &times;
                </button>
              )}
            </div>

            <select
              className="wb-select-compact"
              value={specimenFilter}
              onChange={(e) => setSpecimenFilter(e.target.value)}
            >
              {specimens.map((s) => (
                <option key={s} value={s}>
                  {s === 'ALL' ? 'All Specimens' : s}
                </option>
              ))}
            </select>
          </div>

          {/* Recurrence Warning Banner */}
          {recurringPastEpisodes.length > 0 && (
            <div className="wb-callout-warning" style={{ marginBottom: '14px' }}>
              <span className="wb-callout-icon">⚠️</span>
              <div>
                <strong>Recurrent Pathogen Detected in Patient History</strong>
                <p>
                  Patient has <strong>{recurringPastEpisodes.length} prior culture(s)</strong> isolating{' '}
                  <em>{currentOrganism}</em>. Compare antimicrobial susceptibility profiles below for emerging resistance patterns.
                </p>
              </div>
            </div>
          )}

          <div className="wb-card-clean">
            <div className="wb-table-wrapper">
              <table className="wb-clinical-table">
                <thead>
                  <tr>
                    <th style={{ width: '120px' }}>Order Date</th>
                    <th style={{ width: '150px' }}>Specimen</th>
                    <th>Identified Organism(s)</th>
                    <th style={{ width: '130px' }}>Culture Finding</th>
                    <th>Resistant Antimicrobials</th>
                    <th>AST Summary</th>
                    <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '36px', color: '#94a3b8' }}>
                        No prior microbiology culture records found for this patient UHID.
                      </td>
                    </tr>
                  ) : (
                    filteredHistory.map((h, idx) => (
                      <tr key={idx}>
                        <td>
                          <strong>{new Date(h.orderDate).toLocaleDateString()}</strong>
                          <div className="wb-cell-subtext">
                            {new Date(h.orderDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td>
                          <span className="wb-tag-specimen">{h.specimenName}</span>
                        </td>
                        <td>
                          <strong style={{ color: '#0f172a' }}>
                            <em>{h.organismSummary || 'No Growth Isolated'}</em>
                          </strong>
                        </td>
                        <td>
                          <span className="wb-tag-finding growth">{h.cultureStatus}</span>
                        </td>
                        <td>
                          <span style={{ color: h.resistantDrugsSummary ? '#dc2626' : '#15803d', fontWeight: '600', fontSize: '0.8rem' }}>
                            {h.resistantDrugsSummary || 'None Flagged'}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {h.astSummary || 'Standard Panel'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="wb-tag-status-released">Released</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
